(function() {
  'use strict';

  const LOG_PREFIX = '[LGX Header Cart & Badge Fix]';
  const LGX_DEBUG = false;

  function logDebug(...args) {
    if (LGX_DEBUG) {
      console.log(LOG_PREFIX, ...args);
    }
  }

  function logError(...args) {
    console.error(LOG_PREFIX, ...args);
  }

  const BADGES_SELECTOR = 'hdt-cart-count, hdt-wishlist-count, .hdt-count-box, .hdt-toolbar__count-box';

  let currentAdjustedCount = -1; // Store the last known correct count for cart
  let observer = null; // MutationObserver instance
  let isSelfUpdate = false; // Flag to prevent observer reacting to its own changes

  // Update badge visibility: hide if <= 0 or empty, show if >= 1
  function lgxUpdateBadge(el) {
    if (!el) return;
    const rawText = (el.textContent || '').trim();
    const count = parseInt(rawText, 10);
    const isZero = isNaN(count) || count <= 0;
    logDebug('Evaluating badge visibility:', el.tagName, el.className, 'Raw:', rawText, 'Count:', count, 'IsZero:', isZero);

    el.setAttribute('data-lgx-count', isNaN(count) ? 0 : count);
    if (isZero) {
      if (el.style.display !== 'none') {
        el.style.setProperty('display', 'none', 'important');
      }
      if (!el.hasAttribute('hidden')) {
        el.setAttribute('hidden', '');
      }
      if (!el.classList.contains('lgx-count-hidden')) {
        el.classList.add('lgx-count-hidden');
      }
    } else {
      if (el.style.display === 'none') {
        el.style.removeProperty('display');
      }
      if (el.hasAttribute('hidden')) {
        el.removeAttribute('hidden');
      }
      if (el.classList.contains('lgx-count-hidden')) {
        el.classList.remove('lgx-count-hidden');
      }
    }
  }

  // Refresh visibility of all count badges (cart and wishlist, desktop and mobile)
  function lgxRefreshAllBadges() {
    const badges = document.querySelectorAll(BADGES_SELECTOR);
    logDebug(`Refreshing all count badges (${badges.length} found).`);
    badges.forEach(lgxUpdateBadge);
  }

  // Adjust header cart count (excludes upsell product if configured)
  async function lgxAdjustHeaderCartCount() {
    logDebug('Attempting to adjust header cart count.');
    isSelfUpdate = true;

    const hasUpsellConfig = window.lgxThemeSettings && typeof window.lgxThemeSettings.headerUpsellSourceProductId === 'number';
    const effectiveComparisonProductId = hasUpsellConfig ? window.lgxThemeSettings.headerUpsellSourceProductId : null;

    try {
      const response = await fetch(`${Shopify.routes.root}cart.js`);
      if (!response.ok) {
        logError(`Failed to fetch cart.js, status: ${response.status}`);
        isSelfUpdate = false;
        lgxRefreshAllBadges();
        return;
      }
      const cart = await response.json();

      if (!cart || !cart.items) {
        logError('Invalid cart data received from cart.js.');
        isSelfUpdate = false;
        lgxRefreshAllBadges();
        return;
      }

      let adjustedVisibleItemCount = 0;
      for (const item of cart.items) {
        const itemProductId = parseInt(item.product_id, 10);
        if (effectiveComparisonProductId && itemProductId === effectiveComparisonProductId) {
          logDebug(`   -> EXCLUDING upsell item. Product ID ${itemProductId} === ${effectiveComparisonProductId}`);
        } else {
          adjustedVisibleItemCount += item.quantity;
        }
      }

      logDebug(`Calculated adjusted cart count: ${adjustedVisibleItemCount}. Original cart.item_count: ${cart.item_count}`);
      currentAdjustedCount = adjustedVisibleItemCount;

      const countElements = document.querySelectorAll('hdt-cart-count');
      logDebug(`Found ${countElements.length} element(s) with selector 'hdt-cart-count'.`);

      countElements.forEach((el, index) => {
        if (el.textContent !== adjustedVisibleItemCount.toString()) {
          logDebug(`    -> Updating element ${index} from '${el.textContent}' to '${adjustedVisibleItemCount}'`);
          el.textContent = adjustedVisibleItemCount;
        }
        lgxUpdateBadge(el);
      });

      // Also ensure wishlist badges are refreshed
      lgxRefreshAllBadges();
    } catch (error) {
      logError('Error during cart count adjustment:', error);
      lgxRefreshAllBadges();
    }

    setTimeout(() => { isSelfUpdate = false; }, 0);
  }

  // Setup MutationObserver on all badges (cart + wishlist)
  function setupMutationObserver() {
    const targetNodes = document.querySelectorAll(BADGES_SELECTOR);
    if (targetNodes.length === 0) {
      logDebug('MutationObserver: No badge elements found to observe yet.');
      return;
    }

    if (observer) {
      observer.disconnect();
    }

    const config = { characterData: true, childList: true, subtree: true };

    observer = new MutationObserver((mutationsList) => {
      if (isSelfUpdate) {
        return;
      }

      let needsCartReAdjustment = false;

      for (const mutation of mutationsList) {
        let targetElement = mutation.target;
        if (targetElement.nodeType === Node.TEXT_NODE) {
          targetElement = targetElement.parentElement;
        }

        if (targetElement && typeof targetElement.matches === 'function') {
          if (targetElement.matches(BADGES_SELECTOR)) {
            lgxUpdateBadge(targetElement);
          }

          if (targetElement.matches('hdt-cart-count')) {
            if (currentAdjustedCount !== -1 && targetElement.textContent !== currentAdjustedCount.toString()) {
              logDebug(`MutationObserver: Detected external change on ${targetElement.className}. Text: '${targetElement.textContent}', Expected: '${currentAdjustedCount}'. Re-adjusting.`);
              needsCartReAdjustment = true;
            }
          }
        }
      }

      if (needsCartReAdjustment) {
        lgxAdjustHeaderCartCount();
      }
    });

    targetNodes.forEach(node => {
      observer.observe(node, config);
    });
    logDebug(`MutationObserver: Observing ${targetNodes.length} badge elements.`);
  }

  function initialSetup() {
    lgxRefreshAllBadges();
    lgxAdjustHeaderCartCount().then(() => {
      setupMutationObserver();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialSetup);
  } else {
    initialSetup();
  }

  // Event listeners for cart, wishlist, bfcache, and dynamic section loads
  const events = ['cart:change', 'cart:refresh', 'theme4:wishlist:update', 'pageshow', 'shopify:section:load'];
  events.forEach(function(evt) {
    const handler = function(event) {
      logDebug(`Event '${evt}' detected. Re-adjusting badges.`);
      setTimeout(() => {
        lgxAdjustHeaderCartCount().then(() => {
          setupMutationObserver();
        });
      }, evt === 'cart:change' ? 250 : 100);
    };

    window.addEventListener(evt, handler);
    document.addEventListener(evt, handler);
  });

  logDebug('LGX Header Cart & Badge Fix script initialized.');
})();
